import { useCallback } from 'react';
import { endpoints, type CalendarEvent, type FeedItem } from '../services/api';
import { fetchForecast, type Forecast } from '../services/weather';
import { useSession } from '../store/session';
import { useSettings, type CalendarSource } from '../store/settings';
import { usePolling, type PollState } from './usePolling';

export function useWeather(): PollState<Forecast> {
  const { enabled, location, unit } = useSettings((s) => s.weather);
  const key = enabled && location ? `${location.latitude},${location.longitude},${unit}` : null;
  const load = useCallback((signal: AbortSignal) => fetchForecast(location!, unit, signal), [location, unit]);
  return usePolling(key, load, 30 * 60_000);
}

export interface NewsItem extends FeedItem {
  source: string;
}

export function useNews(): PollState<NewsItem[]> {
  const { enabled, sources } = useSettings((s) => s.news);
  const active = sources.filter((s) => s.enabled);
  const key = enabled && active.length ? active.map((s) => s.url).join('|') : null;
  const load = useCallback(async () => {
    const results = await Promise.allSettled(active.map((s) => endpoints.rss(s.url).then((f) => ({ s, f }))));
    const items = results.flatMap((r) =>
      r.status === 'fulfilled' ? r.value.f.items.slice(0, 12).map((i) => ({ ...i, source: r.value.s.name })) : [],
    );
    if (!items.length && results.some((r) => r.status === 'rejected')) throw new Error('News feeds unavailable');
    // Interleave sources by recency.
    return items.sort((a, b) => (b.published ?? '').localeCompare(a.published ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return usePolling(key, load, 15 * 60_000);
}

export interface AgendaEvent extends CalendarEvent {
  calendar: Pick<CalendarSource, 'name' | 'color'>;
}

const OUTLOOK = { name: 'Outlook', color: '#3b82f6' };

/** True when the calendar widget has something to show: iCal feeds or a connected Outlook. */
export function useHasCalendarSource(): boolean {
  const { sources, outlook } = useSettings((s) => s.calendar);
  const microsoft = useSession((s) => s.microsoft?.connected ?? false);
  return sources.some((s) => s.enabled) || (outlook && microsoft);
}

export function useCalendar(): PollState<AgendaEvent[]> {
  const { enabled, sources, days, outlook } = useSettings((s) => s.calendar);
  const microsoft = useSession((s) => s.microsoft?.connected ?? false);
  const useOutlook = outlook && microsoft;
  const active = sources.filter((s) => s.enabled);
  const key =
    enabled && (active.length || useOutlook)
      ? `${days}|${useOutlook ? 'outlook|' : ''}${active.map((s) => s.url + s.color).join('|')}`
      : null;
  const load = useCallback(async () => {
    const requests: Array<Promise<{ calendar: Pick<CalendarSource, 'name' | 'color'>; events: CalendarEvent[] }>> = [
      ...active.map((s) => endpoints.ics(s.url, days).then((r) => ({ calendar: { name: s.name, color: s.color }, events: r.events }))),
      ...(useOutlook ? [endpoints.outlookCalendar(days).then((r) => ({ calendar: OUTLOOK, events: r.events }))] : []),
    ];
    const results = await Promise.allSettled(requests);
    const events = results.flatMap((res) =>
      res.status === 'fulfilled' ? res.value.events.map((e) => ({ ...e, calendar: res.value.calendar })) : [],
    );
    if (!events.length && results.some((r) => r.status === 'rejected')) throw new Error('Calendars unavailable');
    return events.sort((a, b) => a.start.localeCompare(b.start));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return usePolling(key, load, 10 * 60_000);
}
